import { FC, MouseEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  alpha,
  Box,
  Paper,
  Stack,
  styled,
  Table as MuiTable,
  TableBody,
  TableContainer,
  TableContainerProps,
  TablePagination,
  TablePaginationProps,
  TableProps as MuiTableProps,
  Toolbar,
  toolbarClasses,
} from '@mui/material';
import classNames from 'classnames';

import { TableSkeletonRow } from '@components/base';
import { EmptyState } from '@components/feedback';
import useTypedLocation from '@hooks/useTypedLocation';

import { SelectableTableHead } from './SelectableTableHead';
import { TableHead } from './TableHead';
import { TableRow, TableRowProps } from './TableRow';
import TableSearchField from './TableSearchField';
import { IdKey, SelectableProps, TableHeadProps } from './types';
import { getComparator, getDefaultColumnOrder, Order, stableSort } from './utils';

export type TableProps<T> = IdKey<T> &
  SelectableProps &
  Pick<MuiTableProps, 'size'> &
  Pick<TableHeadProps<T>, 'columns'> &
  Partial<Pick<TableHeadProps<T>, 'loading' | 'order' | 'orderBy'>> & {
    absolutePositioned?: boolean;
    disablePagination?: boolean;
    displayCount?: number;
    emptyStateContext?: 'search' | false;
    filterActions?: ReactNode;
    filterActionsNextToSearch?: boolean;
    filterChips?: ReactNode;
    getSortRank?: (row: T) => number | undefined;
    getTableRowProps?: (row: T) => Omit<TableRowProps<T>, 'highlighted' | 'idKey' | 'item'>;
    hasActions?: boolean;
    hasOptions?: boolean;
    highlightedId?: string;
    items: T[];
    paginationSearchParamKey?: string;
    serverPagination?: {
      hasNextPage: boolean;
      onPageChange: (page: number) => void;
      page: number;
    };
    skeletonCount?: number;
    TableContainerProps?: TableContainerProps;
    TableRowComponent?: FC<TableRowProps<T>>;
    /**
     * If `true`, search field will use URL search params to get/set values, if `false` search will be disabled
     */
    tableSearch?:
      | { debounceMs?: number; queryText: string; setQueryText: (value: string) => void }
      | boolean;
  };

export const StyledTableBody = styled(TableBody)`
  tr:last-of-type td {
    border-bottom: 0;
  }
`;

const rowsPerPage = 100;

const parsePageSearchParam = (value: string | null) => {
  const page = Number(value);
  return Number.isInteger(page) && page > 0 ? page - 1 : 0;
};

const StyledWrapper = styled(Paper)`
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.absolute {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

const StyledToolbar = styled(Toolbar)`
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing()};
  padding: ${({ theme }) => theme.spacing(0, 1.5)} !important;
  min-height: auto !important;
`;

const StyledPagination = styled((props: TablePaginationProps) => (
  <TablePagination component='div' {...props} />
))`
  .${toolbarClasses.root} {
    min-height: 44px;
    padding-left: ${({ theme }) => theme.spacing()};
  }
`;

export function Table<T>({
  absolutePositioned: absolute = true,
  columns,
  disablePagination,
  displayCount,
  emptyStateContext,
  filterActions,
  filterActionsNextToSearch,
  filterChips,
  getSortRank,
  getTableRowProps,
  hasActions,
  hasOptions,
  highlightedId,
  idKey,
  items,
  loading,
  order: initOrder = 'desc',
  orderBy: initOrderBy,
  paginationSearchParamKey,
  serverPagination,
  size,
  selectable,
  skeletonCount = 15,
  TableContainerProps: tableContainerProps,
  TableRowComponent,
  tableSearch,
}: TableProps<T>) {
  const { state } = useTypedLocation<{ highlight?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const [order, setOrder] = useState<Order>(initOrder);
  const [orderBy, setOrderBy] = useState(initOrderBy);
  const routePage = paginationSearchParamKey
    ? parsePageSearchParam(searchParams.get(paginationSearchParamKey))
    : 0;
  const [internalPage, setInternalPage] = useState(0);

  useEffect(() => {
    if (!paginationSearchParamKey && !serverPagination) setInternalPage(0);
  }, [items, paginationSearchParamKey, serverPagination]);

  const setPage = useCallback(
    (newPage: number, replace = false) => {
      if (!paginationSearchParamKey) {
        setInternalPage(newPage);
        return;
      }

      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (newPage > 0) next.set(paginationSearchParamKey, String(newPage + 1));
          else next.delete(paginationSearchParamKey);
          return next;
        },
        { replace }
      );
    },
    [paginationSearchParamKey, setSearchParams]
  );

  const sortedItems = useMemo(() => {
    const compareRank = (a: T, b: T) => {
      const rankA = getSortRank?.(a);
      const rankB = getSortRank?.(b);

      if (rankA == null && rankB == null) return 0;
      return (rankA ?? Number.MAX_SAFE_INTEGER) - (rankB ?? Number.MAX_SAFE_INTEGER);
    };

    const compareColumn = orderBy
      ? getComparator(order, orderBy as keyof T, columns.find(column => column.key === orderBy))
      : undefined;

    let sorted =
      getSortRank || compareColumn
        ? stableSort<T>(items, (a, b) => compareRank(a, b) || compareColumn?.(a, b) || 0)
        : items;

    if (displayCount) sorted = sorted.slice(0, displayCount);
    return sorted;
  }, [items, order, orderBy, displayCount, columns, getSortRank]);

  const requestedPage = serverPagination?.page ?? (paginationSearchParamKey ? routePage : internalPage);
  const maxPage = Math.max(0, Math.ceil(sortedItems.length / rowsPerPage) - 1);
  const page = serverPagination ? requestedPage : Math.min(requestedPage, maxPage);

  useEffect(() => {
    if (serverPagination || !paginationSearchParamKey || loading || requestedPage === page) return;
    setPage(page, true);
  }, [loading, page, paginationSearchParamKey, requestedPage, serverPagination, setPage]);

  const rowsToDisplay = useMemo(
    () =>
      serverPagination
        ? sortedItems.slice(0, rowsPerPage)
        : sortedItems.slice(rowsPerPage * page, rowsPerPage * (page + 1)),
    [sortedItems, page, serverPagination]
  );

  const paginationCount = serverPagination
    ? page * rowsPerPage + rowsToDisplay.length + (serverPagination.hasNextPage ? 1 : 0)
    : sortedItems.length;

  const handleRequestSort = (_: MouseEvent<unknown>, property: string) => {
    if (orderBy === property) setOrder(order === 'desc' ? 'asc' : 'desc');
    else {
      const col = columns.find(column => column.key === property);
      const defaultOrder = getDefaultColumnOrder(col);
      setOrder(defaultOrder);
      setOrderBy(property);
    }
  };

  const highlight = highlightedId ?? state?.highlight ?? '';
  const skeletons = Array.from(Array(Math.min(displayCount ?? skeletonCount, rowsPerPage)).keys());
  const hasToolbar = Boolean(tableSearch || !disablePagination || filterActions || filterChips);
  const RowComponent = TableRowComponent ?? TableRow;

  return (
    <StyledWrapper className={classNames({ absolute })}>
      {hasToolbar && (selectable?.selectedIds || []).length > 0 ? (
        <Toolbar
          sx={{
            pl: { sm: 2 },
            minHeight: { sm: 48 },
            pr: { xs: 1, sm: 1 },
            bgcolor: theme =>
              alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
          }}
        >
          {selectable && <SelectableTableHead idKey={idKey} rows={rowsToDisplay} {...selectable} />}
        </Toolbar>
      ) : (
        <Stack component={Paper} elevation={1} square variant='elevation' width={1}>
          {(tableSearch || !disablePagination || filterActions) && (
            <Stack component={StyledToolbar} direction='row'>
              {filterActionsNextToSearch && tableSearch ? (
                <Stack direction='row' flex={1} maxWidth={548} mr='auto'>
                  <TableSearchField
                    {...(tableSearch === true
                      ? {}
                      : {
                          debounceMs: tableSearch.debounceMs,
                          onSubmit: tableSearch.setQueryText,
                          value: tableSearch.queryText,
                        })}
                  />
                  {filterActions}
                </Stack>
              ) : (
                <>
                  {tableSearch && (
                    <TableSearchField
                      {...(tableSearch === true
                        ? {}
                        : {
                            debounceMs: tableSearch.debounceMs,
                            onSubmit: tableSearch.setQueryText,
                            value: tableSearch.queryText,
                          })}
                      wrapperProps={{ mr: 'auto' }}
                    />
                  )}
                  {filterActions}
                </>
              )}

              {!disablePagination && (
                <StyledPagination
                  count={paginationCount}
                  onPageChange={(_, newPage) => {
                    if (serverPagination) serverPagination.onPageChange(newPage);
                    else setPage(newPage);
                    selectable?.handleToggleAll?.([]);
                  }}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[]}
                />
              )}
            </Stack>
          )}

          {filterChips && (
            <Box
              component={StyledToolbar}
              justifyContent='flex-start !important'
              mb={0.5}
              width={1}
            >
              {filterChips}
            </Box>
          )}
        </Stack>
      )}

      <TableContainer {...tableContainerProps}>
        <MuiTable size={size}>
          <TableHead
            columns={columns}
            hasActions={hasOptions || hasActions}
            loading={loading}
            onRequestSort={handleRequestSort}
            order={order}
            orderBy={orderBy}
            selectable={selectable}
          />

          <StyledTableBody>
            {loading
              ? skeletons.map(skeleton => (
                  <TableSkeletonRow<T>
                    columns={columns}
                    hasActions={hasOptions || hasActions}
                    key={skeleton.toString()}
                    selectable={selectable}
                  />
                ))
              : rowsToDisplay.map(item => {
                  const id = item[idKey] as string;

                  return (
                    <RowComponent
                      columns={columns}
                      hasOptionsButton={hasOptions}
                      highlighted={highlight === id}
                      idKey={idKey}
                      item={item}
                      key={id}
                      selectable={selectable}
                      {...(getTableRowProps ? getTableRowProps(item) : {})}
                    />
                  );
                })}
          </StyledTableBody>
        </MuiTable>

        {!loading && sortedItems.length === 0 && (
          <EmptyState
            description={t('crud.emptyState.description', { context: emptyStateContext })}
            heading={t('crud.emptyState.title')}
            icon={emptyStateContext === 'search' ? 'searchOff' : 'dust'}
          />
        )}
      </TableContainer>
    </StyledWrapper>
  );
}
